import React, { useEffect } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { orgSelector } from '../../reducers/org-selectors';

import { EmployeeMessageTemplateParser } from '../../message-templates/parsers/employee-message-template-parser';
import { MessagePreviewItem } from '../../messages/components/message-preview-item';
import Message from '../legacy-message';

import { MESSAGE_TYPE, MessageType } from '../../util/message-types';

const mapStateToProps = (state: any, { messagePreviewEmployees }: InputProps) => ({
  user: userSelector(state),
  org: orgSelector(state, messagePreviewEmployees[0].orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { messagePreviewEmployees }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(messagePreviewEmployees[0].orgId)),
  },
});

type InputProps = {
  languageCode?: string;
  messageType: string;
  messageBody: string;
  subject: string | undefined;
  messagePreviewEmployees: Employee[];
  urlFileMappings?: Array<UrlFileMapping>;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const MessageComposerPreview = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    languageCode,
    messageType,
    messageBody,
    subject,
    user,
    org,
    messagePreviewEmployees,
    urlFileMappings,
  } = props;
  const senderName = `${user.firstName} ${user.lastName}`;

  useEffect(() => {
    props.actions.loadOrg();
  });

  if (!org || messagePreviewEmployees.length === 0) {
    return null;
  }

  // Instead of parsing url tags like {{{url1}}} to their actual links, keep the actual tags in the preview. There are FileDisplay
  // chips that will indicate which tag corresponds to which link. Thus, we create our own dynamicReplacements here. Note that
  // mustache.js replaces all tags with no mappings to an empty string, so we naively make a dynamic replacement of the form:
  // { url1: '{{{url1}}}', url2: '{{{url2}}}' }
  const dynamicReplacements = urlFileMappings?.reduce(
    (prev, curr) => ({ [curr.urlKey]: `{{{${curr.urlKey}}}}`, ...prev }),
    {},
  );

  let parsedMessage, parsedSubject;
  const templateParser = new EmployeeMessageTemplateParser({
    employee: messagePreviewEmployees[0],
    user,
    org,
    dynamicReplacements,
  });

  try {
    parsedMessage = templateParser.parse(messageBody);
    parsedSubject = subject ? templateParser.parse(subject) : '';
  } catch {
    return (
      <Message
        className="u-margin-top-sm u-margin-bottom-sm"
        variant="warning"
        message="Close all your brackets to see a preview"
        canBeDismissed={false}
      />
    );
  }

  const backgroundColor = 'u-background-actionMuted u-border-neutral6';
  const backgroundStyle = classNames(backgroundColor, 'u-borderRadius-15');
  const messageStyle = classNames(
    backgroundStyle,
    'u-position-absolute u-height-100 u-width-100 u-content',
  );
  const layeredMessageStyle = classNames(messageStyle, 'u-boxShadow-topLeft');

  if (messageType === MESSAGE_TYPE.EMAIL && !subject) {
    return (
      <Message
        className="u-margin-top-sm u-margin-bottom-sm"
        variant="warning"
        message="Add a subject to your email to see a preview"
        canBeDismissed={false}
      />
    );
  }
  return (
    <div
      style={{ width: 'calc(100% - 10px)', left: 10, top: 10 }}
      className="u-margin-bottom-xl u-position-relative"
      data-cy={`text-message-preview${languageCode ? `-${languageCode}` : ''}`}
    >
      <div style={{ top: -10, left: -10, border: '1px solid black' }} className={messageStyle} />
      <div
        style={{ top: -5, left: -5, border: '1px solid black' }}
        className={layeredMessageStyle}
      />
      <MessagePreviewItem
        messageType={messageType as MessageType}
        className="u-boxShadow-topLeft u-position-relative"
        recipientName={`${messagePreviewEmployees.length} employees`}
        sendDateTime={moment().format()}
        messageBody={parsedMessage}
        subject={parsedSubject}
        senderName={
          messageType === MESSAGE_TYPE.EMAIL
            ? senderName
            : `${senderName} to ${messagePreviewEmployees.length} employees`
        }
        includesHTML={messageType === MESSAGE_TYPE.TEXT}
        urlFileMappings={urlFileMappings}
        dataCy={`preview-content${languageCode ? `-${languageCode}` : ''}`}
      />
    </div>
  );
});
